// From https://github.com/Swizec/useDimensions
import type React from 'react';
import { useState, useCallback, useLayoutEffect } from 'react';

type Dimensions = {
  width?: number;
  height?: number;
  top?: number;
  left?: number;
  x?: number;
  y?: number;
  right?: number;
  bottom?: number;
};

function getDimensionObject(
  node: HTMLElement,
  ignoreScroll: boolean
): Dimensions {
  const rect = node.getBoundingClientRect();
  const dimensions = {
    width: rect.width,
    height: rect.height,
    top: rect.y ?? rect.top,
    left: rect.x ?? rect.left,
    x: rect.x ?? rect.left,
    y: rect.y ?? rect.top,
    right: rect.right,
    bottom: rect.bottom,
  };

  if (ignoreScroll) {
    const scrollContainer = node.closest('.tabbed-record__scroll-container');

    if (scrollContainer) {
      dimensions.top += scrollContainer.scrollTop;
      dimensions.left += scrollContainer.scrollLeft;
      dimensions.x += scrollContainer.scrollLeft;
      dimensions.y += scrollContainer.scrollTop;
    }
  }

  return dimensions;
}

function useDimensions(
  {
    liveMeasure,
    ignoreScroll,
  }: { liveMeasure?: boolean; ignoreScroll?: boolean } = {
    liveMeasure: true,
    ignoreScroll: false,
  }
): [React.RefCallback<HTMLElement>, Dimensions, HTMLElement | null] {
  const [dimensions, setDimensions] = useState<Dimensions>({});
  const [node, setNode] = useState<HTMLElement | null>(null);

  const ref = useCallback((node: HTMLElement | null) => {
    setNode(node);
  }, []);

  useLayoutEffect(() => {
    if (node) {
      const measure = () =>
        window.requestAnimationFrame(() =>
          setDimensions(getDimensionObject(node, !!ignoreScroll))
        );
      setDimensions(getDimensionObject(node, !!ignoreScroll));

      if (liveMeasure) {
        window.addEventListener('resize', measure);
        if (!ignoreScroll) window.addEventListener('scroll', measure);

        return () => {
          window.removeEventListener('resize', measure);
          if (!ignoreScroll) window.removeEventListener('scroll', measure);
        };
      }
    }
  }, [liveMeasure, node, ignoreScroll]);

  return [ref, dimensions, node];
}

export default useDimensions;
